import { memo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  IncidentsToolBarGrid,
  FiltersAppliedBadge,
  BadgeCount,
  FilterTitle,
  IncidentsToolbarLeftGrid,
  IncidentsToolbarRightGrid,
  SearchAndStatusBarGrid,
  DesktopToolBar,
  TabletMobileToolBar,
} from "./incidents.styled";

import { Fragment, useState, MouseEvent } from 'react';
import Menu from '@mui/material/Menu';
import CasesFilterIcon from "assets/CasesFilterIcon";
import CasesExportIcon from "assets/CasesExportIcon";
import CustomSort from "core/components/global/customSort/customSort";
import { CircularProgress, Grid, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import AdvanceSearch from "core/components/global/advanceSearch/advanceSearch";
import { useAppSelector } from "core/store/reduxStore";
import { CustomButtonContainer } from "core/components/global/buttons/button/customButton.styled";
import { getIncidentFilters } from "modules/flywheel/services/incidentService";

interface FiltersProps {
  filterData: any;
  setIsFilterModalOpen: (boolean) => void;
  deleteFilter: (arg1: string, arg2: string) => void;
  clearSearch: () => void;
}

function Filters({filterData, setIsFilterModalOpen, deleteFilter, clearSearch}: FiltersProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const { t } = useTranslation();
  
  const handleClickListItem = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <div>
        <CustomButtonContainer
            variant="outlined"
            id="active-filter-menu-toggle"
            aria-expanded={open ? 'true' : undefined}
            onClick={handleClickListItem}
            disabled={Object.entries(filterData["filterTerms"]).length > 0 || filterData["searchTerm"] !== "" ? false : true}
          >
            <FilterTitle className="pe-2 d-flex">{t("INCIDENTS_PAGE.FILTERSAPPLIED")}: </FilterTitle>
            <FiltersAppliedBadge>
              <BadgeCount>{filterData.totalFilters}</BadgeCount>
            </FiltersAppliedBadge>
        </CustomButtonContainer>

        <Menu
          id="active-filter-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          sx={{padding: "0"}}
        >
            <Table aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell></TableCell>
                  <TableCell align="left" variant="head">Column</TableCell>
                  <TableCell align="left" variant="head">Value</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <>
                  {Object.entries(filterData["filterTerms"]).map(([category, categoryValues], index) => {
                    if (typeof categoryValues === 'string') {
                      // If 'value' is a string, split it and generate TableRow elements
                      return (
                        <Fragment key={`filter-item-${category}`}>
                          {categoryValues.split(',').map((value, valueIndex) => (
                            <TableRow key={`${category}-${valueIndex}`}>
                              <TableCell component="th" scope="row">
                                <CloseIcon onClick={() => deleteFilter(category, value)}/>
                              </TableCell>
                              <TableCell align="left">{category.slice(7, -1)}</TableCell>
                              <TableCell align="left">{value.trim()}</TableCell>
                            </TableRow>
                          ))}
                        </Fragment>
                      );
                    } else {
                      // Handle other types of 'value' here
                      return null; // or some other fallback
                    }
                  })}
                  {
                    filterData["searchTerm"] !== "" && <>
                      <TableRow key={`search-item`}>
                        <TableCell component="th" scope="row">
                          <CloseIcon onClick={() => clearSearch()}/>
                        </TableCell>
                        <TableCell align="left">search</TableCell>
                        <TableCell align="left">{filterData["searchTerm"]}</TableCell>
                      </TableRow>
                    </>
                  }
                </>
              </TableBody>
            </Table>
        </Menu>
      </div>
      
      <div>
        <CustomButtonContainer
          variant="outlined"
          data-testid="filter-btn"
          className="all-filters"
          onClick={() => setIsFilterModalOpen(true)}
        >
          <FilterTitle>{t("INCIDENTS_PAGE.ALLFILTERS")}</FilterTitle>
          <CasesFilterIcon />
        </CustomButtonContainer>
      </div>
    </>
  );
}

function Incidents({rowData, headCells, setFilterData, setSearchValue, isFilterModalOpen, setIsFilterModalOpen, setPage, handleExportButton, filterData, deleteFilter, clearSearch}: any) {
  const { t } = useTranslation();
  const isExportLoading = useAppSelector((state) => state.incidents.isExportLoading)
  const headerState = useAppSelector((state) => state.header);

  useEffect(() => {
    setIsFilterModalOpen(headerState.openAllFilters);
  }, [headerState.openAllFilters]);

  return (
    <>
    <DesktopToolBar>
      <SearchAndStatusBarGrid container>
        <Grid item xs>
          <AdvanceSearch filterByApi={true} rowData={rowData} setFilteredData={setSearchValue}/>
        </Grid>
      </SearchAndStatusBarGrid>

      <IncidentsToolBarGrid container className="analystDash-cases-toolbar">
        <IncidentsToolbarLeftGrid item xs className="d-flex flex-row pt-2">
          <Filters
            filterData={filterData}
            setIsFilterModalOpen={setIsFilterModalOpen}
            deleteFilter={deleteFilter}
            clearSearch={clearSearch}
          />
          <CustomSort 
            table_headers={headCells} 
            setFilterData={setFilterData}
            isFilterModalOpen={isFilterModalOpen || false}
            setIsFilterModalOpen={setIsFilterModalOpen}
            setPage={setPage}
            getFiltersFunction={getIncidentFilters}
          />
        </IncidentsToolbarLeftGrid>
        <IncidentsToolbarRightGrid item xs>
          <CustomButtonContainer variant="outlined" onClick={handleExportButton}>
            {t("INCIDENTS_PAGE.EXPORT")}
            {isExportLoading ? <CircularProgress size="1rem" sx={{color: "white"}} /> : <CasesExportIcon />}
          </CustomButtonContainer>
        </IncidentsToolbarRightGrid>
      </IncidentsToolBarGrid>
      </DesktopToolBar>
      
      <TabletMobileToolBar>
        <div className={headerState.openSearch ? 'component-visible' : 'component-hidden'}>
          <AdvanceSearch filterByApi={true} setFilteredData={setSearchValue}/>
        </div>
      </TabletMobileToolBar>
    </>
  );
}

export default memo(Incidents);